import classNames from 'classnames';
import React from 'react';

import {ClickEvent, Tracker} from '@udemy/event-tracking';
import {useI18n} from '@udemy/i18n';
//import {formatNumber} from '@udemy/i18n';
import {QuestionAndAnswer, QuestionsAndAnswers} from '@udemy/react-frequently-asked-questions';
import {useSiteStats} from '@udemy/ud-data';

import styles from './plan-comparison-faq.module.less';

interface PlanComparisonFaqProps {
    showUBLA: boolean;
}

export const PlanComparisonFaq = ({showUBLA}: PlanComparisonFaqProps) => {
    const {getNumericSiteStat} = useSiteStats();
    const {gettext, interpolate} = useI18n();

    const howAreCoursesSelected = {
        question: gettext('How are courses selected for the plans?'),
        answer: interpolate(
            gettext(
                'Each course included in our plans is curated from the %(courseCount)s+ courses in our overall catalog. Our content experts use insights from %(learnerCount)s Udemy learners to identify top-rated, relevant courses on the most sought-after professional topics as well as a selection of personal development topics.',
            ),
            {
                courseCount: getNumericSiteStat('num_courses_rounded'),
                learnerCount: getNumericSiteStat('num_students_million'),
            },
            true,
        ),
    };
    const howIsPPDifferent = {
        question: gettext('How is Personal Plan different from buying a course?'),
        answer: gettext(
            'With Personal Plan, your learning opportunities are endless. You’ll gain monthly access to our top-rated courses, coding exercises, and practice tests in a variety of professional and personal development categories. You can learn one skill or multiple skills at once. Alternatively, if you buy courses one at a time, you’ll get lifetime access to those courses only.',
        ),
    };
    const leaderShipAcademy = {
        question: gettext(
            'How is Leadership Academy different from Personal, Team, and Enterprise Plan?',
        ),
        answer: (
            <div className={styles['faq-answer']}>
                <p>
                    {gettext(
                        'All our plans offer the same core collection of top-rated Udemy courses. Enterprise Plan also includes our international collection with courses in 15 languages, advanced administrative features, and the ability to scale and customize learning for small to large-scale organizations. If you are part of a smaller organization or training an individual team, you can use Team Plan for up to 20 people to get all the course content of Personal Plan plus our basic administrative features.',
                    )}
                </p>
                <p>
                    {gettext(
                        'Leadership Academy is not another plan option but a series of accelerated, guided learning programs that prepare leaders to address today’s most pressing organizational problems. Each program is uniquely built for your team, informed by research, facilitated by a subject-matter expert, and tailored to meet your organization’s specific needs through peer-to-peer engagement.',
                    )}
                </p>
            </div>
        ),
    };
    const whatLanguages = {
        question: gettext('What languages does Enterprise Plan have content for?'),
        answer: gettext(
            'Enterprise Plan includes our international collection of original-language content in 16 languages, including English, Spanish, Portuguese, French, Italian, German, Indonesian, Japanese, Korean, Polish, Russian, Turkish, Arabic, Hindi, Vietnamese, and simplified and traditional Chinese.',
        ),
    };
    const whatIsUPro = {
        question: gettext('What is Udemy Business Pro?'),
        answer: gettext(
            'With the Udemy Business Pro add-on for Enterprise Plan, you get access to a powerful combination of learning tools for your tech learners. In addition to the top-rated courses available with your plan, your employees can also take advantage of hands-on training features like assessments, labs, workspaces, and Udemy paths.',
        ),
    };
    // Only show UBLA-related FAQs in the US
    const faq: QuestionAndAnswer[] = showUBLA
        ? [howAreCoursesSelected, howIsPPDifferent, leaderShipAcademy, whatLanguages, whatIsUPro]
        : [howAreCoursesSelected, howIsPPDifferent, whatLanguages, whatIsUPro];

    const onQuestionToggled = (item: QuestionAndAnswer, index: number) => {
        Tracker.publishEvent(
            new ClickEvent({
                componentName: 'FrequentlyAskedQuestions',
                relatedObjectId: index,
            }),
        );
    };

    return (
        <QuestionsAndAnswers
            className={styles['faq-container']}
            headerClass={classNames('ud-heading-serif-xxl', styles['faq-header'])}
            questionsAndAnswers={faq}
            numRowsToShow="all"
            onQuestionToggled={onQuestionToggled}
        />
    );
};
